import { Info, Loader2, Unlink } from 'lucide-react';
import { useCallback, useState } from 'react';
import { toast } from 'sonner';

import { DestinationPicker } from '@/components/destinations/DestinationPicker';
import { DestinationScopeRow } from '@/components/destinations/DestinationScopeRow';
import type { ManualDestinationFormData } from '@/components/destinations/ManualDestinationForm';
import { ManualDestinationForm } from '@/components/destinations/ManualDestinationForm';
import { DestinationSearchPanel } from '@/components/destinations/search/DestinationSearchPanel';
import { useProviderDestinations } from '@/hooks/useProviderDestinations';
import { useProviderServiceDestinationsUpdateMutation } from '@/services/api/generated/providerDestinationsService';
import type { MedicalProvider } from '@/services/types/treatment-types';
import type { DestinationOption, DestinationScope } from '@/types/provider-destinations';
import {
  DestinationChannelType,
  DestinationScope as DestinationScopeEnum,
  ResearchStatus,
  SaveMode,
  UI_MANUAL_EVIDENCE_TO_API_SOURCE,
} from '@/types/provider-destinations';

interface DestinationSetupSectionProps {
  provider: MedicalProvider;
}

const ALL_SCOPES: DestinationScopeEnum[] = [
  DestinationScopeEnum.RECORDS,
  DestinationScopeEnum.BILLS,
  DestinationScopeEnum.RADIOLOGY,
  DestinationScopeEnum.ER_PHYSICIAN,
];

export const DestinationSetupSection = ({
  provider,
}: DestinationSetupSectionProps) => {
  const {
    scopes,
    getScopeDestinations,
    isLoading,
    isError,
    providerServiceId,
    isLinked,
  } = useProviderDestinations(provider);

  const [setDestination] = useProviderServiceDestinationsUpdateMutation();

  // Panel state
  const [expandedScope, setExpandedScope] = useState<DestinationScope | null>(
    null,
  );
  const [searchScope, setSearchScope] = useState<DestinationScope | null>(null);
  const [manualScope, setManualScope] = useState<DestinationScope | null>(null);

  const handleScopeClick = useCallback(
    (scope: DestinationScope) => {
      setExpandedScope((prev) => (prev === scope ? null : scope));
      setManualScope(null);
    },
    [],
  );

  const handleSelectDestination = useCallback(
    async (scope: DestinationScope, option: DestinationOption) => {
      if (!providerServiceId) return;
      try {
        await setDestination({
          providerServiceId,
          patchedProviderServiceDestinationsPatchRequest: {
            scope,
            contact_point_id: option.contact_point_id,
            save_mode: SaveMode.SAVE_FOR_FUTURE,
          },
        }).unwrap();
        toast.success('Destination updated');
        setExpandedScope(null);
      } catch {
        toast.error('Failed to set destination');
      }
    },
    [providerServiceId, setDestination],
  );

  const handleSearchSelect = useCallback(
    async (option: DestinationOption) => {
      if (!searchScope || !providerServiceId) return;
      try {
        await setDestination({
          providerServiceId,
          patchedProviderServiceDestinationsPatchRequest: {
            scope: searchScope,
            contact_point_id: option.contact_point_id,
            save_mode: SaveMode.SAVE_FOR_FUTURE,
          },
        }).unwrap();
        toast.success('Destination updated');
        setSearchScope(null);
      } catch {
        toast.error('Failed to set destination');
      }
    },
    [searchScope, providerServiceId, setDestination],
  );

  const handleManualSubmit = useCallback(
    async (data: ManualDestinationFormData) => {
      if (!manualScope || !providerServiceId) return;
      try {
        const response = await setDestination({
          providerServiceId,
          patchedProviderServiceDestinationsPatchRequest: {
            scope: manualScope,
            save_mode: data.save_mode,
            manual_destination:
              data.channel_type === DestinationChannelType.MAIL
                ? {
                    channel_type: data.channel_type,
                    address_line1: data.address_line1,
                    address_line2: data.address_line2 || undefined,
                    city: data.city,
                    state: data.state,
                    postal_code: data.postal_code,
                  }
                : {
                    channel_type: data.channel_type,
                    value: data.value,
                  },
            evidence: {
              source_type: UI_MANUAL_EVIDENCE_TO_API_SOURCE[data.evidence_type],
              note: data.evidence_detail?.trim() || undefined,
            },
          },
        }).unwrap();
        const scoped = response.scopes.find((entry) => entry.scope === manualScope);
        if (!scoped?.recommended?.contact_point_id) {
          toast.error('Destination saved but no contact id returned');
          return;
        }
        toast.success('Destination saved');
        setManualScope(null);
        setExpandedScope(null);
      } catch {
        toast.error('Failed to save destination');
      }
    },
    [manualScope, providerServiceId, setDestination],
  );

  // Not linked state
  if (!isLinked) {
    return (
      <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-4">
        <div className="flex items-center gap-2 text-sm text-zinc-500">
          <Unlink className="size-4" />
          <span>
            Link this provider to the directory to manage destinations.
          </span>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="size-5 animate-spin text-zinc-400" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
        Failed to load destinations.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-xs font-medium uppercase tracking-wide text-zinc-500">
          Request Destinations
        </h3>
        {scopes.some((s) => s.research_status === 'IN_PROGRESS') && (
          <div className="flex items-center gap-1.5 text-xs text-blue-600">
            <Loader2 className="size-3 animate-spin" />
            Researching...
          </div>
        )}
      </div>

      <div className="space-y-1">
        {ALL_SCOPES.map((scope) => {
          const block = getScopeDestinations(scope);
          const isExpanded = expandedScope === scope;
          const isManual = manualScope === scope;

          return (
            <div key={scope}>
              <DestinationScopeRow
                scope={scope}
                currentDestination={block?.recommended ?? null}
                coverageStatus={block?.coverage_status ?? 'MISSING'}
                researchStatus={block?.research_status ?? ResearchStatus.NOT_STARTED}
                onEdit={() => handleScopeClick(scope)}
              />

              {isExpanded && providerServiceId && (
                <div className="ml-5 mt-2 mb-3 rounded-lg border border-zinc-200 bg-white p-4">
                  {isManual ? (
                    <ManualDestinationForm
                      onSubmit={handleManualSubmit}
                      onCancel={() => setManualScope(null)}
                    />
                  ) : (
                    <DestinationPicker
                      scope={scope}
                      providerServiceId={providerServiceId}
                      onSelectDestination={(opt) =>
                        handleSelectDestination(scope, opt)
                      }
                      onOpenSearch={() => {
                        setSearchScope(scope);
                      }}
                      onOpenManualEntry={() => {
                        setManualScope(scope);
                      }}
                    />
                  )}

                  {block?.voiceai_followup_phone && (
                    <div className="mt-3 flex items-center gap-2 rounded-md bg-purple-50 px-3 py-2 text-xs text-purple-700">
                      <Info className="size-3.5" />
                      <span>
                        VoiceAI follow-up phone:{' '}
                        <span className="font-medium">
                          {block.voiceai_followup_phone}
                        </span>
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Search panel (Sheet) */}
      {searchScope && providerServiceId && (
        <DestinationSearchPanel
          open={Boolean(searchScope)}
          onOpenChange={(open) => {
            if (!open) setSearchScope(null);
          }}
          providerServiceId={providerServiceId}
          scope={searchScope}
          providerName={provider.name ?? ''}
          onSelectResult={handleSearchSelect}
        />
      )}
    </div>
  );
};
